import React, { useEffect, useState, useCallback } from "react";
import { Card, CardContent } from "./ui/card";
import { BarChart3, TrendingUp, MessageCircle, Loader2, AlertTriangle } from "lucide-react";
import { Badge } from "./ui/badge";
import { useWorkspace } from "../lib/workspaceContext";

// Same-origin in browser so the session cookie (scoped to the page's host) is sent on every API call.
// Falls back to the build-time env var only for SSR / localhost.
const _API_BASE = (typeof window !== 'undefined' && window.location.hostname !== 'localhost') ? window.location.origin : (process.env.REACT_APP_BACKEND_URL || (typeof window !== 'undefined' ? window.location.origin : ''));
const API = `${_API_BASE}/api`;

function fmtMoney(v, currency) {
  if (v == null) return "—";
  try {
    return new Intl.NumberFormat("en-GB", { style: "currency", currency: currency || "GBP", maximumFractionDigits: 0 }).format(v);
  } catch {
    return `${currency || ""} ${v}`;
  }
}

function StatTile({ label, value, sub, dataTestid }) {
  return (
    <Card className="border-slate-200" data-testid={dataTestid}>
      <CardContent className="p-4">
        <p className="text-xs uppercase tracking-wide text-slate-500">{label}</p>
        <p className="text-2xl font-bold text-slate-900 mt-1">{value}</p>
        {sub && <p className="text-xs text-slate-500 mt-1">{sub}</p>}
      </CardContent>
    </Card>
  );
}

export default function OutcomesTab({ workspaceId, onOpenEngagement }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const { currency, region } = useWorkspace();

  const load = useCallback(async () => {
    if (!workspaceId) return;
    setLoading(true);
    try {
      const params = new URLSearchParams();
      if (currency) params.set("currency", currency);
      if (region) params.set("region", region);
      const r = await fetch(`${API}/v2/workspaces/${workspaceId}/outcomes?${params}`, { credentials: "include" });
      if (r.ok) setData(await r.json());
    } finally {
      setLoading(false);
    }
  }, [workspaceId, currency, region]);

  useEffect(() => { load(); }, [load]);

  if (loading) {
    return (
      <Card className="border-slate-200"><CardContent className="p-6 flex items-center gap-2 text-slate-500" data-testid="outcomes-loading">
        <Loader2 className="h-4 w-4 animate-spin" /> Aggregating outcomes…
      </CardContent></Card>
    );
  }
  if (!data) return null;

  const { totals, by_currency, by_region, nps_avg, outcomes_captured, recent_stories, converted_totals, missing_rates, filters } = data;

  return (
    <div className="space-y-4" data-testid="outcomes-tab">
      <p className="text-sm text-slate-500">
        Aggregate outcomes across all engagements.
        {filters?.currency && <span className="ml-1">Currency view: <strong>{filters.currency}</strong>.</span>}
        {filters?.region && <span className="ml-1">Region: <strong>{filters.region}</strong>.</span>}
      </p>

      {converted_totals && (
        <Card className="border-blue-200 bg-blue-50/40" data-testid="outcomes-converted">
          <CardContent className="p-4 flex flex-wrap gap-6 items-baseline">
            <div className="text-xs uppercase tracking-wide text-blue-700">Converted total ({converted_totals.currency})</div>
            <div className="flex gap-6 text-sm">
              <span><span className="text-slate-500 mr-1">Contracted</span><strong>{fmtMoney(converted_totals.contracted, converted_totals.currency)}</strong></span>
              <span><span className="text-slate-500 mr-1">Invoiced</span><strong>{fmtMoney(converted_totals.invoiced, converted_totals.currency)}</strong></span>
              <span><span className="text-slate-500 mr-1">Paid</span><strong>{fmtMoney(converted_totals.paid, converted_totals.currency)}</strong></span>
            </div>
            {(missing_rates || []).length > 0 && (
              <div className="text-xs text-amber-700 flex items-center gap-1 ml-auto" data-testid="outcomes-missing-rates">
                <AlertTriangle className="h-3 w-3" />
                Missing FX rate for: {missing_rates.join(", ")}. Configure in Settings → FX rates.
              </div>
            )}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
        <StatTile dataTestid="outcomes-stat-engagements" label="Engagements" value={totals.engagements} sub={`${totals.active} active · ${totals.completed} completed`} />
        <StatTile dataTestid="outcomes-stat-sessions" label="Sessions delivered" value={totals.sessions_delivered} sub={`of ${totals.sessions_total} planned`} />
        <StatTile dataTestid="outcomes-stat-attendance" label="Attendance" value={totals.attendance_total} sub="cumulative" />
        <StatTile dataTestid="outcomes-stat-nps" label="NPS (avg)" value={nps_avg ?? "—"} sub={outcomes_captured ? `from ${outcomes_captured} outcomes` : "no outcomes captured"} />
        <StatTile dataTestid="outcomes-stat-captured" label="Outcomes captured" value={outcomes_captured} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        <Card className="border-slate-200" data-testid="outcomes-by-currency">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><TrendingUp className="h-4 w-4 text-emerald-600" /> By currency</h3>
            <table className="w-full text-sm">
              <thead><tr className="text-xs uppercase text-slate-500"><th className="text-left">Currency</th><th className="text-right">Contracted</th><th className="text-right">Invoiced</th><th className="text-right">Paid</th></tr></thead>
              <tbody>
                {Object.entries(by_currency).map(([cur, v]) => (
                  <tr key={cur} className="border-t border-slate-100">
                    <td className="py-1 font-medium">{cur}</td>
                    <td className="text-right tabular-nums">{fmtMoney(v.contracted, cur)}</td>
                    <td className="text-right tabular-nums">{fmtMoney(v.invoiced, cur)}</td>
                    <td className="text-right tabular-nums">{fmtMoney(v.paid, cur)}</td>
                  </tr>
                ))}
                {Object.keys(by_currency).length === 0 && (
                  <tr><td colSpan="4" className="text-xs text-slate-400 py-2">No data yet.</td></tr>
                )}
              </tbody>
            </table>
          </CardContent>
        </Card>

        <Card className="border-slate-200" data-testid="outcomes-by-region">
          <CardContent className="p-4">
            <h3 className="text-sm font-semibold text-slate-700 mb-2 flex items-center gap-2"><BarChart3 className="h-4 w-4 text-blue-600" /> By region</h3>
            <ul className="space-y-1 text-sm">
              {Object.entries(by_region).map(([r, c]) => (
                <li key={r} className="flex justify-between border-t border-slate-100 py-1">
                  <span className="text-slate-700">{r}</span>
                  <Badge variant="outline" className="text-xs">{c}</Badge>
                </li>
              ))}
              {Object.keys(by_region).length === 0 && <li className="text-xs text-slate-400">No data yet.</li>}
            </ul>
          </CardContent>
        </Card>
      </div>

      <Card className="border-slate-200" data-testid="outcomes-recent-stories">
        <CardContent className="p-4">
          <h3 className="text-sm font-semibold text-slate-700 mb-3 flex items-center gap-2"><MessageCircle className="h-4 w-4 text-purple-600" /> Recent win stories</h3>
          {recent_stories.length === 0 ? (
            <p className="text-xs text-slate-400">No stories captured yet. Capture them from the Outcomes section of any engagement.</p>
          ) : (
            <ul className="space-y-3">
              {recent_stories.map((s) => (
                <li
                  key={s.outcome_id}
                  className="border border-slate-200 rounded p-3 hover:border-blue-300 cursor-pointer"
                  onClick={() => onOpenEngagement?.(s.engagement_id)}
                  data-testid={`outcome-story-${s.outcome_id}`}
                >
                  <div className="flex items-baseline justify-between gap-2">
                    <span className="font-medium text-slate-800 text-sm">{s.engagement_name}</span>
                    <div className="flex items-center gap-2 text-xs text-slate-500">
                      {s.client_name && <span>{s.client_name}</span>}
                      {s.nps_score != null && <Badge variant="outline" className="text-xs">NPS {s.nps_score}</Badge>}
                    </div>
                  </div>
                  {s.win_story_md && <p className="text-sm text-slate-600 mt-1 whitespace-pre-line">{s.win_story_md}</p>}
                  {s.follow_on_md && <p className="text-xs text-amber-700 mt-1">Follow-on: {s.follow_on_md}</p>}
                </li>
              ))}
            </ul>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
